import { useEffect, useState } from "react";
import { BellRing, Loader2, Send, Share, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/pages/admin/AdminLayout";

const ROLES = [["all", "Semua pengguna"], ["member", "Jemaat"], ["pastor", "Pastor / Leadership"],
  ["ministry_leader", "Ministry Leader"], ["community_leader", "Community Leader"], ["event_admin", "Event Admin"],
  ["content_admin", "Content Admin"], ["finance", "Finance"]];

export default function PushSettings() {
  const [data, setData] = useState(null);
  const [os, setOs] = useState({ onesignal_enabled: false, onesignal_app_id: "", onesignal_api_key: "" });
  const [msg, setMsg] = useState({ title: "", body: "", link: "/portal", role: "all" });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/notify/push").then((r) => {
    setData(r.data);
    setOs({ onesignal_enabled: r.data.onesignal.enabled, onesignal_app_id: r.data.onesignal.app_id, onesignal_api_key: "" });
  });
  useEffect(() => { load(); }, []);
  if (!data) return <Skeleton className="h-96" />;

  const saveOs = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/notify/push", os);
      toast.success("Pengaturan provider push disimpan");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const testPush = async () => {
    try {
      const { data: r } = await api.post("/admin/notify/test-push");
      toast.success(`Notifikasi uji terkirim ke ${r.sent} perangkat`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const broadcast = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/notify/broadcast", msg);
      toast.success(`Terkirim ke ${r.recipients} jemaat (${r.pushed} push perangkat)`);
      setMsg({ ...msg, title: "", body: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="admin-push-settings">
      <AdminTitle eyebrow="Sistem" title="Notifikasi & Aplikasi (PWA)">
        <button onClick={testPush} data-testid="push-test-button" className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-xl px-4 py-2.5 text-sm font-bold"><BellRing className="w-4 h-4" /> Uji Push ke Akun Ini</button>
      </AdminTitle>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100" data-testid="push-native-card">
          <p className="font-extrabold text-slate-900">Web Push Bawaan (VAPID)</p>
          <p className="text-xs text-slate-500 mt-1">Aktif tanpa biaya & tanpa layanan pihak ketiga. Jemaat cukup menekan "Aktifkan Notifikasi" di Portal.</p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-slate-50 p-4"><p className="text-3xl font-black text-slate-900" data-testid="push-devices-count">{data.devices_count}</p><p className="text-xs text-slate-500">Perangkat berlangganan</p></div>
            <div className="rounded-2xl bg-emerald-50 p-4"><p className="text-sm font-black text-emerald-700">Aktif</p><p className="text-xs text-emerald-700/70 mt-1">Kunci VAPID otomatis dibuat server</p></div>
          </div>
          <p className="mt-4 text-[11px] font-mono break-all text-slate-400" data-testid="push-vapid-key">{data.vapid_public}</p>
          <div className="mt-5 max-h-64 overflow-y-auto divide-y divide-slate-100">
            {data.devices.map((d, i) => (
              <div key={i} className="py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`push-device-${i}`}>
                <span className="font-bold text-slate-800 truncate">{d.name}<span className="text-slate-400 font-normal"> · {d.nig || "-"}</span></span>
                <span className="text-xs text-slate-400 shrink-0">{d.created_at ? fmtDate(d.created_at.slice(0, 10)) : ""}</span>
              </div>
            ))}
            {data.devices.length === 0 && <p className="py-3 text-sm text-slate-400" data-testid="push-devices-empty">Belum ada perangkat berlangganan.</p>}
          </div>
        </div>

        <form onSubmit={saveOs} className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="push-onesignal-card">
          <p className="font-extrabold text-slate-900">Provider Pihak Ketiga · OneSignal (opsional)</p>
          <p className="text-xs text-slate-500">Dipakai sebagai cadangan bila perangkat jemaat tidak berlangganan push bawaan. Ambil App ID & App API Key di dashboard OneSignal → Settings → Keys & IDs.</p>
          <label className="flex items-center gap-3 text-sm font-bold text-slate-700">
            <Switch checked={os.onesignal_enabled} onCheckedChange={(v) => setOs({ ...os, onesignal_enabled: v })} data-testid="onesignal-enabled-switch" />
            Aktifkan OneSignal
          </label>
          <input className={inputCls} placeholder="OneSignal App ID" value={os.onesignal_app_id} onChange={(e) => setOs({ ...os, onesignal_app_id: e.target.value })} data-testid="onesignal-appid-input" />
          <input type="password" autoComplete="new-password" className={inputCls} placeholder={data.onesignal.api_key_masked ? `App API Key tersimpan (${data.onesignal.api_key_masked})` : "App API Key"} value={os.onesignal_api_key} onChange={(e) => setOs({ ...os, onesignal_api_key: e.target.value })} data-testid="onesignal-apikey-input" />
          <button disabled={busy} className={`${btnPrimary} w-full`} data-testid="onesignal-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Provider"}</button>
        </form>

        <form onSubmit={broadcast} className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="push-broadcast-card">
          <p className="font-extrabold text-slate-900">Kirim Notifikasi ke Jemaat</p>
          <input className={inputCls} placeholder="Judul notifikasi" value={msg.title} onChange={(e) => setMsg({ ...msg, title: e.target.value })} required data-testid="broadcast-title-input" />
          <textarea rows={3} className={inputCls} placeholder="Isi pesan" value={msg.body} onChange={(e) => setMsg({ ...msg, body: e.target.value })} required data-testid="broadcast-body-input" />
          <div className="grid sm:grid-cols-2 gap-3">
            <select className={inputCls} value={msg.role} onChange={(e) => setMsg({ ...msg, role: e.target.value })} data-testid="broadcast-role-select">
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select className={inputCls} value={msg.link} onChange={(e) => setMsg({ ...msg, link: e.target.value })} data-testid="broadcast-link-select">
              <option value="/portal">Beranda Portal</option>
              <option value="/portal/renungan">Renungan</option>
              <option value="/portal/events">Event & Tiket</option>
              <option value="/portal/grow">Grow · RC & TTS</option>
              <option value="/portal/ministry">My Ministry</option>
              <option value="/portal/elearning">E-Learning</option>
              <option value="/portal/kehadiran">Kehadiran</option>
              <option value="/portal/doa">Pokok Doa</option>
              <option value="/portal/profile">Profil</option>
            </select>
          </div>
          <button disabled={sending} className={`${btnPrimary} w-full`} data-testid="broadcast-send-button">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Kirim Notifikasi</>}</button>
        </form>

        <div className="bg-slate-900 text-white rounded-3xl p-6 md:p-8" data-testid="push-pwa-card">
          <Smartphone className="w-6 h-6 text-blue-300" />
          <p className="mt-3 font-extrabold">Install sebagai Aplikasi</p>
          <div className="mt-4 space-y-4 text-sm text-white/70">
            <div>
              <p className="font-bold text-white">Android / Chrome</p>
              <p className="text-xs mt-1">Buka website → menu ⋮ → <b>Install app</b> / <b>Tambahkan ke layar utama</b>. Notifikasi langsung aktif setelah jemaat menekan "Aktifkan Notifikasi".</p>
            </div>
            <div>
              <p className="font-bold text-white flex items-center gap-1.5"><Share className="w-3.5 h-3.5" /> iPhone / Safari</p>
              <p className="text-xs mt-1">Buka di Safari → tombol Bagikan → <b>Add to Home Screen</b>. iOS 16.4+ hanya mengirim push jika aplikasi dibuka dari ikon layar utama (bukan dari Safari).</p>
            </div>
            <p className="text-[11px] text-white/40">Push memerlukan domain HTTPS. Di domain gereja sendiri, semua fitur ini berjalan otomatis.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
